"use client";

import { styled } from "@mui/system";
import { Box, Typography } from "@mui/material";
import { VideoPlayer } from "./UI";
import { GalleryItemPayloadV2 } from "@/types/gallery";
import DownloadButton from "./sharing/DownloadButton";
import CopyLinkButton from "./sharing/CopyLinkButton";
import { useEffect, useState } from "react";
import MoreButton from "./sharing/MoreButton";
import { trackEvent } from "@/app/utils/analytics";
import ShareFileButton from "@/app/components/sharing/ShareFileButton";
import { FILE_TYPE_ENUM } from "@/app/components/UI/VideoPlayer";
import FileTypeChoose from "@/app/components/FileTypeChoose";
import RemixButton from "@/app/components/sharing/RemixButton";

const MemoWrapper = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "12px"
});

const FooterWrapper = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
  maxWidth: "400px"
});

// const UserInfo = styled(Box)({
//   fontSize: "14px",
//   fontWeight: 600,
//   color: "#e6e6e6"
// });

const IconGroup = styled(Box)({
  display: "flex",
  justifyItems: "center",
  alignItems: "center",
  "& button": {
    padding: 0
  }
});

export default function MemoItem({ memo, withScore = false }: { memo: GalleryItemPayloadV2; withScore: boolean }) {
  const [shareUrl, setShareUrl] = useState<string>("");
  const [canUseShareApi, setCanUseShareApi] = useState(false);
  const [currentFileType, setCurrentFileType] = useState<FILE_TYPE_ENUM>(FILE_TYPE_ENUM.video);
  const isGif = currentFileType === FILE_TYPE_ENUM.image;

  useEffect(() => {
    try {
      if (typeof window === "undefined") return;
      const url = new URL(`/meme/${memo.id}`, window.location.origin);
      setShareUrl(url.toString());

      // Check if browser supports Web Share API
      setCanUseShareApi(!!navigator.share);
    } catch (error) {
      console.error("Error constructing share URL:", error);
      // Fallback to basic URL construction
      setShareUrl(`${window.location.origin}/meme/${memo.id}`);
    }
  }, [memo]);

  return (
    <MemoWrapper>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: "auto",
          display: "flex",
          justifyContent: "center"
        }}
      >
        <Box
          sx={{
            opacity: isGif ? 1 : 0,
            transition: "opacity 0.3s ease",
            pointerEvents: isGif ? "auto" : "none",
            height: isGif ? "auto" : 0,
            overflow: "hidden",
            width: isGif ? "100%" : 0,
            display: "flex",
            justifyContent: "center"
          }}
        >
          <VideoPlayer
            src={memo.previewGifUrl}
            height={memo.previewGifHeight}
            width={memo.previewGifWidth}
            fileType={FILE_TYPE_ENUM.image}
            hasAudio={memo.hasAudio}
          />
        </Box>
        <Box
          sx={{
            opacity: isGif ? 0 : 1,
            transition: "opacity 0.3s ease",
            pointerEvents: isGif ? "none" : "auto",
            visibility: isGif ? "hidden" : "visible",
            width: isGif ? 0 : "100%",
            display: "flex",
            justifyContent: "center"
          }}
        >
          <VideoPlayer
            src={memo.previewMp4Url}
            height={memo.previewMp4Height}
            width={memo.previewMp4Width}
            fileType={FILE_TYPE_ENUM.video}
            hasAudio={memo.hasAudio}
          />
        </Box>
      </Box>

      <FooterWrapper>
        {
          withScore ? (
            <Typography variant="body1">
              <strong>Trending Score:</strong> {memo.trendingScore}
            </Typography>
          ) : null
          // <UserInfo>{user?.displayName ? user?.displayName : "Community Member"}</UserInfo>
        }

        <Box
          sx={{
            width: "100%",
            display: "flex",
            gap: 1,
            flexDirection: "column"
          }}
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between"
            }}
          >
            <IconGroup>
              <CopyLinkButton url={shareUrl} showLabel={false} memeId={memo.id} />
              <DownloadButton memeId={memo.id} source="share_group" showLabel={false} />
              {canUseShareApi && <ShareFileButton shareUrl={shareUrl} memeId={memo.id} showLabel={false} />}
              {memo.hasOwnership && <MoreButton meme={memo} showLabel={false} />}
            </IconGroup>
            <Box
              sx={{
                width: "50%",
                display: "flex",
                justifyContent: "flex-end"
              }}
            >
              {memo.hasOwnership ? (
                <FileTypeChoose
                  value={currentFileType}
                  onChange={(v: FILE_TYPE_ENUM) => {
                    trackEvent(`meme_item_file_type_choose_${v}`, {});
                    setCurrentFileType(v);
                  }}
                />
              ) : (
                <RemixButton
                  memeId={memo.id}
                  previewMp4Url={memo.previewMp4Url}
                  variant={"outlined"}
                  supportUseAnimationAsTemplate={memo.supportUseAnimationAsTemplate}
                  hasAudio={memo.hasAudio}
                  wrapperSx={{
                    width: "100%"
                  }}
                  sx={{
                    width: "100%"
                  }}
                />
              )}
            </Box>
          </Box>

          {memo.hasOwnership && (
            <RemixButton
              memeId={memo.id}
              previewMp4Url={memo.previewMp4Url}
              hasAudio={memo.hasAudio}
              variant={"outlined"}
              supportUseAnimationAsTemplate={memo.supportUseAnimationAsTemplate}
              sx={{
                width: "100%"
              }}
            />
          )}
        </Box>
      </FooterWrapper>
    </MemoWrapper>
  );
}
